/**
 * The open item: a line with a name and price typed at the till, for goods that are not in the
 * catalogue (a one-off, a service, a local item with no barcode).
 *
 * Every sale line must name a product, so open items point at one reserved system product. Its id
 * is fixed, so it is the same row in the cloud database and in a desktop till's local database.
 * It is inactive and marked `is_system`, which keeps it out of search, stock, purchasing, import
 * and the product editor. An open item never moves stock.
 *
 * It carries no tax unless the cashier adds one ("Item Tax"). Whether the typed price already
 * includes that tax is a store setting (`pos.open_item_price_includes_tax`, default yes: the price
 * typed is the price paid).
 */

export const OPEN_ITEM_PRODUCT_ID = 'product:open-item';
export const OPEN_ITEM_SKU = '__OPEN_ITEM__';
export const OPEN_ITEM_NAME_MAX = 60;

/** Trimmed, single-spaced and NFC-normalised, so the same Tamil name typed twice is the same text. */
export function normalizeOpenItemName(raw: string): string {
  if (typeof raw !== 'string') return '';
  return raw.normalize('NFC').replace(/\s+/g, ' ').trim();
}

/** Why a typed name cannot be used, or null. */
export function openItemNameProblem(raw: string): string | null {
  const name = normalizeOpenItemName(raw);
  if (name.length === 0) return 'Type a name for the item.';
  if ([...name].length > OPEN_ITEM_NAME_MAX) return `Keep the name to ${OPEN_ITEM_NAME_MAX} characters.`;
  return null;
}

export function isOpenItem(productId: string | null | undefined): boolean {
  return productId === OPEN_ITEM_PRODUCT_ID;
}
